import { AppDispatch, AppThunkAction } from '../index';
import { globalErrorResponse, showNotification, showNotificationWithInfo, TYPE_ERROR } from '../notifications/actions';
import { Price, SubscriptionPeriod, SubscriptionPeriodType } from '../../../models/Article';
import { UsageCondition } from '../../../models/Product';
import { ListSourceType } from '../../../models/ListSourceType';
import { invalidateCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { disableListLoading, enableListLoading } from '../listLoading/actions';
import i18n from '../../../i18n';
import { RemoveBulkByLineItemKeysRequest } from '../../api/selection-list/removeBulkFromListByLineItemKeys';
import { removeProductsFromCartByLineItemKeys } from '../../api/shopping-cart/removeProductsFromCartByLineItemKeys';
import Utils from '../../shared/utils';
import { convertLineItemKeysToDto } from '../../shared/utils/convertLineItemKey';
import { ListContentSource } from '../constants';
import {
    IncludeProductSelection,
    isExcludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { TFunction } from 'i18next';
import { UnavailableProductOut } from '../api';
import * as consts from '../constants';

export type UpdateProductOptions = {
    /**
     * The unique ID of a product, e.g. `"BOL_000001"`.
     */
    readonly id: string;

    /**
     * The human-readable title of the product identified by the given `id`.
     */
    readonly title?: string;

    readonly sourceUsageCondition?: UsageCondition;

    readonly price?: Price;

    readonly subscriptionPeriodType?: SubscriptionPeriodType;
    readonly subscriptionPeriod?: SubscriptionPeriod;

    readonly targetSubscriptionPeriodType?: SubscriptionPeriodType;
    readonly sourceSubscriptionPeriodType?: SubscriptionPeriodType;

    readonly source?: ListSourceType;
    readonly sourceId?: string;
};

type FailAddToCartParams = {
    t: TFunction;
    source?: ListContentSource;
    error: any;
    productSelection?: ProductSelection;
    onSuccess?: () => void;
};

export function failUpdateToCart({
    t,
    error,
    source,
    productSelection,
    onSuccess
}: FailAddToCartParams): AppThunkAction {
    return (dispatch: AppDispatch) => {
        if (!error.runtimeDuplication) {
            switch (error.response.status) {
                case 409: {
                    const data = error.response.data;
                    if (data.additionalInfo) {
                        const infoData = data.additionalInfo.map(p => Utils.withdrawnProductLine(p)).join('<br/>');
                        const isSelectedAllRequest = isExcludeProductSelection(productSelection);

                        const failMessage = isSelectedAllRequest
                            ? t('notifications.failAddToCart.fail')
                            : t('notifications.failAddToCart.proceed');
                        const showCancelButton = !isSelectedAllRequest;

                        dispatch(
                            showNotificationWithInfo(
                                data.message,
                                failMessage,
                                infoData,
                                data.additionalInfo,
                                showCancelButton
                                    ? () =>
                                          withdrawnCallback({
                                              dispatch,
                                              source,
                                              unavailableProducts: data.additionalInfo,
                                              onSuccess
                                          })
                                    : null,
                                showCancelButton
                            )
                        );
                        break;
                    }
                    dispatch(globalErrorResponse(error));
                    break;
                }
                default:
                    dispatch(globalErrorResponse(error));
            }
        }
    };
}

type WithdrawnCallbackParams = {
    dispatch: AppDispatch;
    source: ListContentSource | undefined;
    unavailableProducts: UnavailableProductOut[];
    onSuccess?: () => void;
};

//TODO: consider renaming since it now also removes comingSoonProducts
export async function withdrawnCallback({
    dispatch,
    source,
    unavailableProducts,
    onSuccess = () => {}
}: WithdrawnCallbackParams) {
    if (source && [consts.SOURCE_CART, consts.SOURCE_SELECTIONLIST].includes(source)) {
        if (source === consts.SOURCE_CART) {
            dispatch(
                removeFromCart(
                    {
                        included: unavailableProducts.map(({ lineItemKey }) => lineItemKey)
                    },
                    onSuccess
                )
            );
        }
    }
}

export function removeFromCart(productSelection: IncludeProductSelection, onSuccess: () => void): AppThunkAction {
    return (dispatch: AppDispatch) => {
        const body: RemoveBulkByLineItemKeysRequest = {
            lineItemKeys: convertLineItemKeysToDto(productSelection.included)
        };

        dispatch(enableListLoading());
        removeProductsFromCartByLineItemKeys(body)
            .then(() => {
                dispatch(showNotification(i18n.t('notifications.removeFromCart.success')));
                invalidateCartSummaryQuery().then();
                invalidateCartGroupedItems().then();
                invalidateShoppingCartProducts().then();
                invalidateCartAggregations().then();
                onSuccess();
            })
            .catch(error => {
                dispatch(globalErrorResponse(error));
                return Promise.reject();
            })
            .finally(() => {
                dispatch(disableListLoading());
            });
    };
}

export const showOrdersNotAllowedNotification = () =>
    showNotification(i18n.t('notifications.showOrdersNotAllowedNotification.error'), TYPE_ERROR);
